<template>
  <div class="w-full">
    <div v-if="label.length > 0" class="flex items-center mb-1 space-x-2">
      <span class="text-xs tracking-wide uppercase label leading-none flex items-center" :class="{ required }">
        {{ label }}
        <span v-if="multipleLimit > 0"> (Limit to {{ multipleLimit }})</span>
      </span>

      <span
        :title="t('attributeChooser.title')"
        class="flex items-center justify-center w-4 h-4 cursor-pointer hover:text-primary-500"
        @click="openDialog"
      >
        <font-awesome-icon icon="fa-light fa-table" class="w-4 h-4" />
      </span>
    </div>
    <div
      class="border border-gray-300 rounded px-3 py-2 cursor-pointer min-h-[40px] flex items-center flex-wrap gap-2 bg-white"
      @click="openDialog"
    >
      <span
        v-for="item in selectedItemsObjects"
        :key="item[itemKeyProp]"
        class="block p-1 text-xs truncate border rounded-md cursor-default"
      >
        {{ item[itemDisplayProp] }}
      </span>
    </div>

    <tx-dialog
      v-model="dialogVisible"
      :title="title"
      width="500px"
    >
      <div class="overflow-hidden">
        <tx-list-transfer
          v-model:selected-items="selectedItems"
          :available-items-label="availableItemsLabel"
          :selected-items-label="selectedItemsLabel"
          :items="items"
          :item-key-prop="itemKeyProp"
          :item-display-prop="itemDisplayProp"
          :multiple-limit="multipleLimit"
          class="max-h-96"
        />
        <div class="flex justify-between mt-4">
          <tx-button type="cancel" :text="t('attributeChooser.addAll')" @click="addAll" />
          <tx-button type="cancel" :text="t('attributeChooser.removeAll')" @click="removeAll" />
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-center px-6 py-4 space-x-3 sm:justify-end bg-gray-50">
          <tx-button type="confirm" :text="t('general.save')" width="100px" @click="save" />
          <tx-button type="cancel" :text="t('general.cancel')" width="100px" @click="cancel" />
        </div>
      </template>
    </tx-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TxButton from './TxButton.vue'
import TxListTransfer from '@/shared/components/txListTransfer'
import TxDialog from '@/shared/components/TxDialog.vue'

interface IProps {
  selectedItems: any[]
  items: any[]
  itemKeyProp: string
  itemDisplayProp: string
  availableItemsLabel?: string
  selectedItemsLabel?: string
  title?: string
  label?: string
  multipleLimit?: number
  required?: boolean
  selectedByDefault?: string[]
}

const props = withDefaults(defineProps<IProps>(), {
  label: '',
  multipleLimit: 0,
  required: false,
})
const emit = defineEmits(['update:selectedItems'])

const { t } = useI18n()

const dialogVisible = ref(false)
const selectedItems = ref<any[]>([])

const availableItemsLabel = computed(() => props.availableItemsLabel || t('attributeChooser.availableAttributes'))
const selectedItemsLabel = computed(() => props.selectedItemsLabel || t('attributeChooser.selectedAttributes'))
const title = computed(() => props.title || t('attributeChooser.title'))
const selectedItemsObjects = computed(() => {
  if (!Array.isArray(props.selectedItems)) { return [] }

  // when the selected items are string array, normally from template options default value
  if (typeof props.selectedItems[0] === 'string') {
    return props.selectedItems
      .map(key => props.items.find(itm => itm[props.itemKeyProp] === key))
      .filter(Boolean)
  }

  // objects
  return props.selectedItems.filter(sel =>
    props.items.some(itm => itm[props.itemKeyProp] === sel[props.itemKeyProp]),
  )
})

function openDialog() {
  selectedItems.value = [...selectedItemsObjects.value]
  dialogVisible.value = true
}

function save() {
  dialogVisible.value = false
  emit('update:selectedItems', selectedItems.value.map(item => item[props.itemKeyProp]))
}

function cancel() {
  dialogVisible.value = false
}

function addAll() {
  selectedItems.value = [...props.items]
}

function removeAll() {
  selectedItems.value = []
}
</script>
